import React, { useState, useEffect } from 'react';
import { 
  Terminal, 
  Database, 
  Settings, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  Edit,
  Trash,
  Save,
  ChevronDown,
  ChevronRight,
  FileCode,
  Package,
  Coffee,
  Server,
  RefreshCw,
  Loader,
  AlertCircle,
  Monitor,
  Cpu,
  HardDrive,
  Globe,
  Copy,
  Check,
  FileX,
  Upload,
  FolderOpen
} from 'lucide-react';

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Import dynamique de tous les fichiers JSON du répertoire components
const componentContext = require.context('../data/components', false, /\.json$/);

// Mapping des icônes dynamiques
const ICON_MAPPING = {
  'Globe': Globe,
  'Server': Server,
  'Database': Database,
  'Settings': Settings,
  'Monitor': Monitor,
  'Cpu': Cpu,
  'HardDrive': HardDrive,
  'Package': Package,
  'Coffee': Coffee,
  'Terminal': Terminal
};

// Systèmes d'exploitation par défaut
const DEFAULT_OPERATING_SYSTEMS = [
  {
    id: 'ubuntu',
    name: 'Ubuntu/Debian',
    icon: '🐧',
    color: 'orange'
  },
  {
    id: 'rhel',
    name: 'RHEL/CentOS',
    icon: '🎩',
    color: 'red'
  },
  {
    id: 'windows',
    name: 'Windows Server',
    icon: '🪟',
    color: 'blue'
  }
];

// Helper pour obtenir l'icône
const getIconComponent = (iconName, fallback = Settings) => {
  return ICON_MAPPING[iconName] || fallback;
};

// Helper pour détecter le type de composant depuis les métadonnées
const detectComponentType = (metadata) => {
  const title = metadata?.title?.toLowerCase() || '';
  const component = metadata?.component?.toLowerCase() || '';
  const description = metadata?.description?.toLowerCase() || '';
  
  // Détection par mots-clés
  if (title.includes('backend') || title.includes('spring') || title.includes('java') || component.includes('backend')) {
    return {
      icon: 'Server',
      color: 'green',
      category: 'Backend'
    };
  }
  
  if (title.includes('frontend') || title.includes('nginx') || title.includes('react') || component.includes('frontend')) {
    return {
      icon: 'Globe',
      color: 'blue',
      category: 'Frontend'
    };
  }
  
  if (title.includes('database') || title.includes('postgresql') || title.includes('mysql') || component.includes('database')) {
    return {
      icon: 'Database',
      color: 'purple',
      category: 'Database'
    };
  }
  
  if (title.includes('agent') || component.includes('agent')) {
    return {
      icon: 'Settings',
      color: 'orange',
      category: 'Agent'
    };
  }
  
  if (title.includes('monitoring') || title.includes('prometheus') || title.includes('grafana')) {
    return {
      icon: 'Monitor',
      color: 'yellow',
      category: 'Monitoring'
    };
  }
  
  // Détection par défaut
  return {
    icon: 'Package',
    color: 'gray',
    category: 'Other'
  };
};

// Validation de structure d'un composant d'installation
const validateComponentStructure = (data, filename) => {
  const requiredFields = ['metadata', 'platforms'];
  const errors = [];
  
  // Vérifier les champs obligatoires
  for (const field of requiredFields) {
    if (!data[field]) {
      errors.push(`Champ obligatoire manquant: ${field}`);
    }
  }
  
  // Vérifier la structure des métadonnées
  if (data.metadata && !data.metadata.title) {
    errors.push('Métadonnées: titre manquant');
  }
  
  // Vérifier qu'au moins une plateforme est définie
  if (data.platforms && Object.keys(data.platforms).length === 0) {
    errors.push('Au moins une plateforme doit être définie');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

// Extraire les métadonnées d'un composant
const extractComponentMetadata = (data, filename) => {
  const metadata = data.metadata || {};
  const typeInfo = detectComponentType(metadata);
  
  // Générer un ID unique basé sur le nom du fichier
  const id = filename.replace('.json', '').replace(/[^a-zA-Z0-9]/g, '_');
  
  return {
    id,
    filename,
    name: metadata.title || filename.replace('.json', '').replace(/[_-]/g, ' '),
    icon: typeInfo.icon,
    description: metadata.description || `Composant d'installation ${typeInfo.category}`,
    color: typeInfo.color,
    category: typeInfo.category,
    version: metadata.version || '1.0.0',
    lastUpdate: metadata.lastUpdate || new Date().toISOString()
  };
};

// Composant pour afficher une commande avec fonctionnalité de copie
const CommandBlock = ({ command, description, note, platform }) => {
  const [copied, setCopied] = useState(false);

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy command:', err);
    }
  };

  return (
    <div className="my-4">
      {description && (
        <div className="text-sm text-gray-600 mb-2 flex items-center gap-2">
          <span>{description}</span>
          {platform && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
              {platform}
            </span>
          )}
        </div>
      )}
      <div className="relative">
        <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre className="whitespace-pre-wrap">{command}</pre>
        </div>
        <button
          onClick={copyCommand}
          className={`absolute right-2 top-2 p-2 rounded-md transition-colors ${
            copied ? 'text-green-400' : 'text-gray-400 hover:text-gray-300'
          }`}
          title="Copier la commande"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      {note && (
        <div className="text-sm text-gray-500 mt-2 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{note}</span>
        </div>
      )}
    </div>
  );
};

// Composant pour les étapes d'installation
const InstallationSteps = ({ componentData, selectedOS }) => {
  if (!componentData || !componentData.platforms) return null;

  const platformData = componentData.platforms[selectedOS];
  if (!platformData) return null;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900">
          Installation - {platformData.name}
        </h3>
        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          {platformData.name}
        </span>
      </div>
      
      {platformData.steps && platformData.steps.map((step, index) => (
        <Card key={step.id || index}>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <div className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center font-semibold">
                {index + 1}
              </div>
              <span>{step.title}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {step.description && (
                <p className="text-gray-600">{step.description}</p>
              )}
              
              {step.commands && step.commands.map((cmd, cmdIndex) => (
                <CommandBlock
                  key={cmdIndex}
                  command={cmd.command}
                  description={cmd.description}
                  note={cmd.note}
                  platform={selectedOS}
                />
              ))}

              {step.verification && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <h4 className="font-medium text-green-900">Vérification</h4>
                  </div>
                  <div className="bg-gray-900 text-gray-100 rounded p-2 font-mono text-sm">
                    {step.verification}
                  </div>
                </div>
              )}

              {step.troubleshooting && step.troubleshooting.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <h4 className="font-medium text-yellow-900">Dépannage</h4>
                  </div>
                  <ul className="list-disc list-inside space-y-1">
                    {step.troubleshooting.map((item, idx) => (
                      <li key={idx} className="text-yellow-800 text-sm">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {step.configFiles && step.configFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Fichiers de configuration</h4>
                  <div className="space-y-3">
                    {step.configFiles.map((file, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          <FileCode className="h-4 w-4 text-blue-500" />
                          <span className="font-medium text-sm">{file.path}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{file.description}</p>
                        {file.content && (
                          <CommandBlock
                            command={file.content}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Section post-installation */}
      {componentData.postInstallation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <span>Tests post-installation</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {componentData.postInstallation.map((test, index) => (
                <div key={index}>
                  <h4 className="font-medium text-gray-900 mb-2">{test.title}</h4>
                  <CommandBlock command={test.command} />
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Résultat attendu:</strong> {test.expectedOutput}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section des ports */}
      {componentData.ports && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-6 w-6 text-blue-600" />
              <span>Ports réseau</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {componentData.ports.map((port, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-mono font-bold text-lg">{port.port}</span>
                    <p className="text-sm text-gray-600">{port.description}</p>
                  </div>
                  <div className="text-green-600">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Composant de statut de découverte
const DiscoveryStatus = ({ discoveredCount, scannedCount, onRetry }) => {
  return (
    <Card className="mb-6 border-blue-200 bg-blue-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FolderOpen className="h-5 w-5 text-blue-600" />
            <div>
              <h4 className="font-medium text-blue-900">Auto-découverte des composants</h4>
              <p className="text-sm text-blue-700">
                {discoveredCount} composant(s) trouvé(s) sur {scannedCount} fichiers analysés
              </p>
            </div>
          </div>
          <Button
            onClick={onRetry}
            variant="outline"
            size="sm"
            className="text-blue-600 border-blue-300 hover:bg-blue-100"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-scanner
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Composant principal
const ComponentBasedInstallation = () => {
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedOS, setSelectedOS] = useState('ubuntu');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discoveryStats, setDiscoveryStats] = useState({ discovered: 0, scanned: 0 });

  // Auto-découverte des composants au montage
  useEffect(() => {
    autoDiscoverComponents();
  }, []);

  const autoDiscoverComponents = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Démarrage de l\'auto-découverte des composants...');
      
      const keys = componentContext.keys();
      const discoveredFiles = [];
      const failedFiles = [];
      
      // Parcourir tous les fichiers trouvés
      keys.forEach(key => {
        const filename = key.replace('./', '');
        
        try {
          // Charger le fichier
          const data = componentContext(key);
          
          // Valider la structure
          const validation = validateComponentStructure(data, filename);
          
          if (validation.isValid) {
            // Extraire les métadonnées
            const metadata = extractComponentMetadata(data, filename);
            
            discoveredFiles.push({
              ...metadata,
              data,
              icon: metadata.icon
            });
          } else {
            failedFiles.push({
              filename,
              errors: validation.errors,
              type: 'validation'
            });
          }
        } catch (error) {
          failedFiles.push({
            filename,
            errors: [error.message],
            type: 'loading'
          });
        }
      });
      
      console.log(`📁 Découvert ${discoveredFiles.length} composants valides`);
      
      // Trier par catégorie puis par nom
      discoveredFiles.sort((a, b) => {
        if (a.category !== b.category) {
          return a.category.localeCompare(b.category);
        }
        return a.name.localeCompare(b.name);
      });

      setComponents(discoveredFiles);
      setDiscoveryStats({
        discovered: discoveredFiles.length,
        scanned: keys.length
      });

      if (discoveredFiles.length > 0) {
        setSelectedComponent(discoveredFiles[0]);
        console.log(`✅ Premier composant sélectionné: ${discoveredFiles[0].name}`);
      } else {
        console.log('⚠️ Aucun composant valide trouvé');
      }

      // Log des fichiers avec erreurs (pour debug)
      if (failedFiles.length > 0) {
        console.log('⚠️ Fichiers avec erreurs:', failedFiles);
      }

    } catch (err) {
      setError('Erreur lors de l\'auto-découverte des composants');
      console.error('Error during auto-discovery:', err);
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-500 text-white',
      green: 'bg-green-500 text-white',
      purple: 'bg-purple-500 text-white',
      orange: 'bg-orange-500 text-white',
      red: 'bg-red-500 text-white',
      yellow: 'bg-yellow-500 text-white',
      gray: 'bg-gray-500 text-white'
    };
    return colorMap[color] || colorMap.blue;
  };

  const getOutlineColorClasses = (color) => {
    const colorMap = {
      blue: 'border-blue-500 text-blue-700 bg-blue-50',
      green: 'border-green-500 text-green-700 bg-green-50',
      purple: 'border-purple-500 text-purple-700 bg-purple-50',
      orange: 'border-orange-500 text-orange-700 bg-orange-50',
      red: 'border-red-500 text-red-700 bg-red-50',
      yellow: 'border-yellow-500 text-yellow-700 bg-yellow-50',
      gray: 'border-gray-500 text-gray-700 bg-gray-50'
    };
    return colorMap[color] || colorMap.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Auto-découverte des composants...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent>
              <div className="flex items-center space-x-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
              <Button
                onClick={autoDiscoverComponents}
                className="mt-4"
                variant="outline"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer la découverte
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (components.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent>
              <div className="text-center py-8">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucun composant trouvé
                </h3>
                <p className="text-gray-600 mb-4">
                  Placez vos fichiers JSON dans <code>/data/components/</code>
                </p>
                <Button
                  onClick={autoDiscoverComponents}
                  variant="outline"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Re-scanner le dossier
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Installation des Composants
            </h1>
            <p className="text-gray-600">
              Auto-découverte et installation de tous les composants disponibles
            </p>
          </div>
          <Button
            variant="outline"
            onClick={autoDiscoverComponents}
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Re-scanner</span>
          </Button>
        </div>
      </div>

      {/* Statut de découverte */}
      <DiscoveryStatus 
        discoveredCount={discoveryStats.discovered}
        scannedCount={discoveryStats.scanned}
        onRetry={autoDiscoverComponents}
      />

      {/* Sélection de composant */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Composants disponibles ({components.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {components.map((component) => {
            const IconComponent = getIconComponent(component.icon);
            const isSelected = selectedComponent?.id === component.id;
            return (
              <button
                key={component.id}
                onClick={() => setSelectedComponent(component)}
                className={`p-4 rounded-lg text-left transition-all duration-200 border-2 ${
                  isSelected 
                    ? getColorClasses(component.color)
                    : `border-gray-200 bg-white hover:${getOutlineColorClasses(component.color)} hover:border-${component.color}-300`
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <IconComponent className={`h-6 w-6 ${isSelected ? 'text-white' : `text-${component.color}-600`}`} />
                  <div className="flex-1">
                    <h3 className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                      {component.name}
                    </h3>
                    <div className={`text-xs ${isSelected ? 'text-white/70' : 'text-gray-500'}`}>
                      {component.category}
                    </div>
                  </div>
                </div>
                <p className={`text-sm ${isSelected ? 'text-white/90' : 'text-gray-600'}`}>
                  {component.description}
                </p>
                <div className={`text-xs mt-2 font-mono ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>
                  {component.filename}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedComponent && selectedComponent.data && (
        <>
          {/* Informations du composant */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                {(() => {
                  const IconComponent = getIconComponent(selectedComponent.icon);
                  return <IconComponent className={`h-6 w-6 text-${selectedComponent.color}-500`} />;
                })()}
                <span>{selectedComponent.data.metadata?.title || selectedComponent.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                {selectedComponent.data.metadata?.description || selectedComponent.description}
              </p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>Fichier: {selectedComponent.filename}</span>
                {selectedComponent.data.metadata?.version && (
                  <>
                    <span>•</span>
                    <span>Version: {selectedComponent.data.metadata.version}</span>
                  </>
                )}
                {selectedComponent.data.metadata?.lastUpdate && (
                  <>
                    <span>•</span>
                    <span>Mis à jour: {selectedComponent.data.metadata.lastUpdate}</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sélection du système d'exploitation */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Système d'exploitation</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {DEFAULT_OPERATING_SYSTEMS.map((os) => {
                const isSelected = selectedOS === os.id;
                const isAvailable = selectedComponent.data.platforms && selectedComponent.data.platforms[os.id];
                
                return (
                  <button
                    key={os.id}
                    onClick={() => isAvailable && setSelectedOS(os.id)}
                    disabled={!isAvailable}
                    className={`p-4 rounded-lg text-left transition-all duration-200 border-2 ${
                      !isAvailable 
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected 
                          ? `bg-${os.color}-500 text-white border-${os.color}-500 shadow-lg`
                          : `border-gray-200 bg-white hover:border-${os.color}-300 hover:shadow-md`
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-2xl">{os.icon}</span>
                      <h3 className={`font-semibold ${
                        !isAvailable ? 'text-gray-400' : isSelected ? 'text-white' : 'text-gray-900'
                      }`}>
                        {os.name}
                      </h3>
                    </div>
                    {!isAvailable && (
                      <p className="text-sm text-gray-400">Guide non disponible</p>
                    )}
                    {isAvailable && isSelected && (
                      <p className="text-sm text-white/90">Guide sélectionné</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Guide d'installation */}
          {selectedComponent.data.platforms && selectedComponent.data.platforms[selectedOS] && (
            <InstallationSteps 
              componentData={selectedComponent.data}
              selectedOS={selectedOS}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ComponentBasedInstallation;